// openingMoveOffer — the pure client predicate: should the agent's opening-move
// offer (an EXPLICIT-START affordance) be shown on the conversation surface?
//
// The aha for a new author is the agent drafting their first grounded material
// (.claude/analysis/onboarding-activation.md §1). The server owns the
// authoritative `shouldOfferOpeningMove(D.State, conversationId)`; the UI holds
// no domain state, so it decides only WHETHER TO OFFER, from its projected
// view-models: at least one source exists to ground a first draft on, and the
// conversation is still pristine (no entry yet — neither the author's words nor
// an agent opening). Showing the offer authors NO command and calls NO model;
// the turn fires only when the author accepts (EXPLICIT-START, IDD 9436bedc).
//
// Pristine == empty transcript: the synthetic seed that drives an ACCEPTED
// opening move is suppressed from this projection (conversationAdapter), so an
// accepted offer yields a non-empty transcript (the agent reply) and the offer
// retires on its own — no separate "already offered" flag to track.
//
// Pure + total: `(inputs) → boolean`, no I/O, no throw.

export type { OpeningMoveOfferInputs };
export { shouldShowOpeningMoveOffer };

import type { ChatEntry } from "../components/Chat/types.js";

type OpeningMoveOfferInputs = {
    // At least one source exists to ground the agent's first draft on.
    readonly hasSource: boolean;
    // The conversation transcript as the UI holds it (seed-suppressed).
    readonly transcript: readonly ChatEntry[];
};

const shouldShowOpeningMoveOffer = ({
    hasSource,
    transcript,
}: OpeningMoveOfferInputs): boolean => hasSource && transcript.length === 0;
